import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import './LocationPage.css';

const LocationPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    latitude: '',
    longitude: '',
    locationId: ''
  });

  // Google Maps API key - you'll need to add this to your environment variables
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  // Check if Google Maps is available
  const isGoogleMapsAvailable = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 0;

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const locationsRef = collection(db, 'locations');
      const snapshot = await getDocs(locationsRef);
      const locationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLocations(locationsList);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateLocationId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `LOC${timestamp}${random}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const handleAddressSearch = async (address) => {
    if (!GOOGLE_MAPS_API_KEY) {
      alert('Google Maps API key not configured');
      return;
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        setFormData(prev => ({
          ...prev,
          latitude: location.lat.toString(),
          longitude: location.lng.toString()
        }));
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.address) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const locationData = {
        ...formData,
        locationId: formData.locationId || generateLocationId(),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingLocation) {
        // Update existing location
        await updateDoc(doc(db, 'locations', editingLocation.id), {
          ...locationData,
          updatedAt: new Date()
        });
        setEditingLocation(null);
      } else {
        // Add new location
        await addDoc(collection(db, 'locations'), locationData);
      }

      setFormData({
        companyName: '',
        address: '',
        latitude: '',
        longitude: '',
        locationId: ''
      });
      setShowAddModal(false);
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Error saving location. Please try again.');
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      companyName: location.companyName,
      address: location.address,
      latitude: location.latitude,
      longitude: location.longitude,
      locationId: location.locationId
    });
    setShowAddModal(true);
  };

  const handleDelete = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await deleteDoc(doc(db, 'locations', locationId));
        fetchLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
        alert('Error deleting location. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: '',
      address: '',
      latitude: '',
      longitude: '',
      locationId: ''
    });
    setEditingLocation(null);
    setShowAddModal(false);
  };

  return (
    <div className="location-page">
      <div className="location-header">
        <h1>Location Management</h1>
        <button 
          className="add-location-btn"
          onClick={() => setShowAddModal(true)}
        >
          <FaPlus /> Add New Location
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading locations...</p>
        </div>
      ) : (
        <div className="locations-grid">
          {locations.length === 0 ? (
            <div className="no-locations">
              <FaMapMarkerAlt size={48} />
              <h3>No Locations Found</h3>
              <p>Add your first location to get started</p>
            </div>
          ) : (
            locations.map((location) => (
              <div key={location.id} className="location-card">
                <div className="location-card-header">
                  <h3>{location.companyName}</h3>
                  <div className="location-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => handleEdit(location)}
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(location.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                <div className="location-card-body">
                  <p className="location-address">
                    <FaMapMarkerAlt /> {location.address}
                  </p>
                  <p className="location-coordinates">
                    Lat: {location.latitude}, Lng: {location.longitude}
                  </p>
                  <p className="location-id">
                    ID: {location.locationId}
                  </p>
                </div>
                                 {location.latitude && location.longitude && isGoogleMapsAvailable && (
                   <div className="location-map">
                     <iframe
                       width="100%"
                       height="200"
                       frameBorder="0"
                       src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${location.latitude},${location.longitude}`}
                       allowFullScreen
                     />
                   </div>
                 )}
                 {location.latitude && location.longitude && !isGoogleMapsAvailable && (
                   <div className="location-map-placeholder">
                     <p>Google Maps not configured</p>
                     <p>Coordinates: {location.latitude}, {location.longitude}</p>
                   </div>
                 )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Location Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingLocation ? 'Edit Location' : 'Add New Location'}</h2>
              <button className="modal-close-btn" onClick={resetForm}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="location-form">
                <div className="form-group">
                  <label htmlFor="companyName">Company Name *</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                    placeholder="Enter company name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address *</label>
                  <div className="address-input-group">
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                      rows="3"
                      placeholder="Enter full address"
                    />
                    <button
                      type="button"
                      className="search-coordinates-btn"
                      onClick={() => handleAddressSearch(formData.address)}
                      disabled={!formData.address || !GOOGLE_MAPS_API_KEY}
                    >
                      Search Coordinates
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="locationId">Location ID</label>
                  <input
                    type="text"
                    id="locationId"
                    name="locationId"
                    value={formData.locationId}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Auto-generated if left empty"
                  />
                </div>

                <div className="coordinates-section">
                  <h4>Location Coordinates</h4>
                  <p>Click on the map below to set the location coordinates</p>
                  
                  <div className="coordinates-inputs">
                    <div className="form-group">
                      <label htmlFor="latitude">Latitude</label>
                      <input
                        type="number"
                        id="latitude"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        className="form-control"
                        step="any"
                        placeholder="Click map to set"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="longitude">Longitude</label>
                      <input
                        type="number"
                        id="longitude"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        className="form-control"
                        step="any"
                        placeholder="Click map to set"
                      />
                    </div>
                  </div>

                                     {isGoogleMapsAvailable ? (
                     <div className="map-container">
                       <iframe
                         width="100%"
                         height="300"
                         frameBorder="0"
                         src={`https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_API_KEY}&center=${formData.latitude || 0},${formData.longitude || 0}&zoom=15`}
                         allowFullScreen
                       />
                       <p className="map-instructions">
                         Click on the map to set the location coordinates
                       </p>
                     </div>
                   ) : (
                     <div className="map-placeholder">
                       <p>Google Maps API key not configured</p>
                       <p>Please add VITE_GOOGLE_MAPS_API_KEY to your environment variables</p>
                       <p>You can still manually enter coordinates below</p>
                     </div>
                   )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    <FaSave /> {editingLocation ? 'Update Location' : 'Save Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPage; 